import { V1_BASE_URL } from '@app/data/constants';
import { type TaskBaseStatusEnum } from '@data/tasks/constants';
import { listTasks, type ListTasksResponseType } from '@queries/task/listTasks';
import { listTaskTemplates, type TaskTemplateType } from '@queries/task/listTaskTemplates';
import { transformGraphqlResponse } from '@repo/utils';
import baseService, { TAG_TYPES } from '@services/baseService';

interface TaskListPropType {
  leadUuid?: string;
  ownerUuid?: string;
  status?: TaskBaseStatusEnum;
  id?: string;
  cursor?: string;
  limit?: number;
}

const tasksService = baseService.injectEndpoints({
  endpoints: (build) => ({
    listTasks: build.query<ListTasksResponseType, TaskListPropType | void>({
      query: (body) => ({
        method: 'POST',
        url: '/graphql',
        body: {
          query: listTasks,
          variables: body,
        },
      }),
      providesTags: [TAG_TYPES.taskList],
      transformResponse: (response) => transformGraphqlResponse(response)?.tasks,
    }),
    listTaskTemplates: build.query<TaskTemplateType[], void>({
      query: () => ({
        method: 'POST',
        url: '/graphql',
        body: {
          query: listTaskTemplates,
        },
      }),
      transformResponse: (response) => transformGraphqlResponse(response)?.taskTemplates ?? [],
      keepUnusedDataFor: Infinity,
    }),
    updateTaskStatus: build.mutation<void, { id: string; status: TaskBaseStatusEnum }>({
      query: ({ id, status }) => ({
        method: 'PATCH',
        url: `${V1_BASE_URL}tasks/${id}/update_status`,
        body: { status },
      }),
      invalidatesTags: [TAG_TYPES.taskList],
    }),
    createTask: build.mutation<void, any>({
      query: (body) => ({
        method: 'POST',
        url: `${V1_BASE_URL}tasks`,
        body,
      }),
      invalidatesTags: [TAG_TYPES.taskList],
    }),
    editTaskProperties: build.mutation<void, { task: any; id: string; }>({
      query: ({ task, id }) => ({
        method: 'PUT',
        url: `${V1_BASE_URL}tasks/${id}/update`,
        body: task,
      }),
      invalidatesTags: [TAG_TYPES.taskList],
    }),
  }),
});

export const {
  useLazyListTasksQuery,
  useLazyListTaskTemplatesQuery,
  useListTaskTemplatesQuery,
  useUpdateTaskStatusMutation,
  useCreateTaskMutation,
  useEditTaskPropertiesMutation,
} = tasksService;

export default tasksService;
